import React, { useState } from 'react';
import axios from 'axios';
import { Plus, UserCheck, UserX, Trash2, Pencil, Lock, Check, Settings2, Eye, EyeOff, Shield, RefreshCcw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import RoleModules from './RoleModules';
import ModuleDetails from './ModuleDetails';
import useGranularPermissions from '../../hooks/useGranularPermissions';

import { PERMISSIONS_DEFS } from './RolesAndPermissionsView';

// Helper pour aplatir les permissions (ex: caisse: { entrees: {} } -> 'caisse.entrees': {})
const flattenPermissions = (perms) => {
  const flat = {};
  for (const [key, val] of Object.entries(perms)) {
    if (val && typeof val === 'object' && !Array.isArray(val) && Object.keys(val).some(k => typeof val[k] === 'object')) {
      for (const [subKey, subVal] of Object.entries(val)) {
        flat[`${key}.${subKey}`] = subVal;
      }
    } else {
      flat[key] = val;
    }
  }
  return flat;
};

// Helper pour reconstruire l'objet imbriqué avant la sauvegarde
const unflattenPermissions = (flatPerms) => {
  const nested = {};
  for (const [key, val] of Object.entries(flatPerms)) {
    if (key.includes('.')) {
      const [parent, child] = key.split('.');
      if (!nested[parent]) nested[parent] = {};
      nested[parent][child] = val;
    } else {
      nested[key] = val;
    }
  }
  return nested;
};



const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

function UsersTab({ users, role, pharmacy, currentUser, onRefresh }) {
  const { hasPermission, canCreate, canUpdate, canDelete } = useGranularPermissions();
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    username: '',
    password: '',
    confirm_password: '',
    full_name: '',
    email: '',
    phone: '',
    role: role
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [userPermissions, setUserPermissions] = useState({});
  const [selectedModule, setSelectedModule] = useState(null);
  const [savingPermissions, setSavingPermissions] = useState(false);

  const openPermissionsDialog = async (user) => {
    setSelectedUser(user);
    // On charge les permissions
    try {
      const headers = { 'X-User-Id': currentUser.id };
      const res = await axios.get(`${API}/users/${user.id}/granular-permissions`, { headers });
      setUserPermissions(flattenPermissions(res.data.granular_permissions || {}));
      setSelectedModule(PERMISSIONS_DEFS.revenues);
      setPermissionsDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des permissions');
    }
  };

  const handlePermissionChange = (moduleId, permId, value) => {
    setUserPermissions(prev => ({
      ...prev,
      [moduleId]: {
        ...(prev[moduleId] || {}),
        [permId]: value
      }
    }));
  };

  const setModuleFullAccess = (moduleId) => {
    const moduleDef = PERMISSIONS_DEFS[moduleId];
    const newModulePerms = {};
    if (moduleDef.gestion) moduleDef.gestion.forEach(p => newModulePerms[p.id] = true);
    if (moduleDef.consultation) moduleDef.consultation.forEach(p => newModulePerms[p.id] = true);
    setUserPermissions(prev => ({
      ...prev,
      [moduleId]: newModulePerms
    }));
  };

  const handleSaveUserPermissions = async () => {
    try {
      setSavingPermissions(true);
      await axios.put(
        `${API}/users/${selectedUser.id}/granular-permissions`,
        { granular_permissions: unflattenPermissions(userPermissions) },
        { headers: getHeaders() }
      );
      toast.success('Permissions enregistrées avec succès');
      setPermissionsDialogOpen(false);
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingPermissions(false);
    }
  };

  const [profileFormData, setProfileFormData] = useState({
    full_name: '',
    username: '',
    email: '',
    phone: '',
  });

  const getHeaders = () => ({
    'X-User-Id': currentUser?.id || '',
    'X-Pharmacy-Id': pharmacy.id || '',
    'X-Session-Token': currentUser?.session_token || ''
  });

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (newUserData.password !== newUserData.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    try {
      const userData = {
        ...newUserData,
        pharmacy_id: pharmacy.id
      };
      await axios.post(`${API}/users`, userData, { headers: getHeaders() });
      toast.success('Utilisateur créé avec succès !');
      setCreateUserDialogOpen(false);
      setNewUserData({ username: '', password: '', confirm_password: '', full_name: '', email: '', phone: '', role: role });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de l\'utilisateur');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`⚠️ ATTENTION : Êtes-vous sûr de vouloir supprimer le compte de "${userName}" ?`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`, { headers: getHeaders() });
      toast.success('Utilisateur supprimé avec succès');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/users/${userId}`, { is_active: !currentStatus }, { headers: getHeaders() });
      toast.success(`Statut modifié avec succès`);
      onRefresh();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const openPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setPasswordDialogOpen(true);
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword !== confirmPassword) {
      toast.error('Veuillez vérifier vos mots de passe');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Minimum 6 caractères');
      return;
    }
    try {
      await axios.put(`${API}/users/${selectedUser.id}/password`, { new_password: newPassword }, { headers: getHeaders() });
      toast.success('Mot de passe modifié');
      setPasswordDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const openProfileDialog = (user) => {
    let currentRole = user.role || 'caissier';
    if (user.pharmacies && Array.isArray(user.pharmacies)) {
      const pharm = user.pharmacies.find(p => p.pharmacy_id === pharmacy?.id);
      if (pharm && pharm.role) {
        currentRole = pharm.role;
      }
    }
    
    // Normalize to match SelectItem values (lowercase)
    currentRole = typeof currentRole === 'string' ? currentRole.toLowerCase().trim() : 'caissier';
    
    const validRoles = ['admin', 'gerant', 'comptable', 'caissier', 'assistant'];
    if (!validRoles.includes(currentRole) && currentRole !== 'super_admin' && currentRole !== 'owner') {
      currentRole = 'caissier';
    }

    setSelectedUser(user);
    setProfileFormData({
      full_name: user.full_name || '',
      username: user.username || '',
      email: user.email || '',
      phone: user.phone || '',
      role: currentRole,
    });
    setProfileDialogOpen(true);
  };

  const handleProfileSubmit = async () => {
    try {
      await axios.put(
        `${API}/users/${selectedUser.id}/profile`,
        {
          ...profileFormData,
          pharmacy_id: pharmacy.id, // keep them in this pharmacy
        },
        { headers: getHeaders() }
      );
      toast.success('Profil mis à jour');
      setProfileDialogOpen(false);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-bold text-slate-800 text-lg">Utilisateurs avec ce rôle</h3>
        {canCreate('users') && (
          <Button 
            onClick={() => setCreateUserDialogOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un utilisateur
          </Button>
        )}
      </div>

      <div className="overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-100 mt-4">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50/50 border-b border-gray-100">
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Utilisateur</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Statut</th>
              <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map(user => (
              <tr key={user.id} className="hover:bg-gray-50/50 transition-colors group">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold text-sm mr-3 flex-shrink-0">
                      {user.username ? user.username.charAt(0).toUpperCase() : (user.full_name ? user.full_name.charAt(0).toUpperCase() : 'U')}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                        {user.full_name || user.username}
                        {user.permissions_explicitly_set && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded border border-orange-100" title="Ce rôle a des accès modifiés manuellement">
                            <Shield className="w-3 h-3" /> Accès personnalisé
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">@{user.username}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-600">{user.email || '-'}</div>
                  <div className="text-xs text-gray-500">{user.phone || '-'}</div>
                </td>
                <td className="px-6 py-4 text-center whitespace-nowrap">
                  {user.is_active ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 font-normal">Actif</Badge>
                  ) : (
                    <Badge className="bg-red-50 text-red-700 border border-red-200 font-normal">Inactif</Badge>
                  )}
                </td>
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  <div className="flex items-center justify-end space-x-2">
                    {canUpdate('users') && (
                      <button 
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        onClick={() => openProfileDialog(user)}
                        title="Éditer"
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    {canUpdate('users') && (
                      <button 
                        className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                        onClick={() => openPasswordDialog(user)}
                        title="Mot de passe"
                      >
                        <Lock size={16} />
                      </button>
                    )}
                    {hasPermission('users', 'manage_permissions') && (
                      <button 
                        className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors"
                        onClick={() => openPermissionsDialog(user)}
                        title="Accès"
                      >
                        <Settings2 size={16} />
                      </button>
                    )}
                    {user.id !== currentUser?.id && canDelete('users') && (
                      <button 
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        onClick={() => handleDeleteUser(user.id, user.username)}
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                  Aucun utilisateur trouvé.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Dialogs */}
      {/* Add User Dialog */}
      <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden border-0 shadow-2xl rounded-2xl" aria-describedby={undefined}>
          <DialogHeader className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-6 m-0">
            <DialogTitle className="text-2xl font-bold text-white">Créer un utilisateur</DialogTitle>
            <DialogDescription className="text-emerald-100 mt-1 text-sm">
              Ajoutez un employé dans votre pharmacie
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} autoComplete="off" className="p-6 space-y-6 bg-gray-50/50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="full_name" className="text-sm font-semibold text-gray-700">Nom complet *</Label>
                <Input id="full_name" name="full_name" required value={newUserData.full_name} onChange={e => setNewUserData({...newUserData, full_name: e.target.value})} autoComplete="off" className="h-11 rounded-xl border-gray-200 bg-white" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-semibold text-gray-700">Nom d'utilisateur *</Label>
                <Input id="username" name="username" required value={newUserData.username} onChange={e => setNewUserData({...newUserData, username: e.target.value})} autoComplete="new-password" placeholder="" className="h-11 rounded-xl border-gray-200 bg-white" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold text-gray-700">Mot de passe *</Label>
                <div className="relative">
                  <Input id="password" name="password" type={showPassword ? "text" : "password"} required value={newUserData.password} onChange={e => setNewUserData({...newUserData, password: e.target.value})} autoComplete="new-password" placeholder="" className="h-11 rounded-xl border-gray-200 bg-white pr-10" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Confirmer le mot de passe *</Label>
                <div className="relative">
                  <Input type={showConfirmPassword ? "text" : "password"} required value={newUserData.confirm_password} onChange={e => setNewUserData({...newUserData, confirm_password: e.target.value})} autoComplete="new-password" placeholder="" className="h-11 rounded-xl border-gray-200 bg-white pr-10" />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Rôle</Label>
                <Select value={newUserData.role} onValueChange={(val) => setNewUserData({...newUserData, role: val})}>
                  <SelectTrigger className="h-11 rounded-xl border-gray-200 bg-white">
                    <SelectValue placeholder="Sélectionner un rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrateur</SelectItem>
                    <SelectItem value="gerant">Gestionnaire</SelectItem>
                    <SelectItem value="comptable">Comptable</SelectItem>
                    <SelectItem value="caissier">Caissier</SelectItem>
                    <SelectItem value="assistant">Assistant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Email</Label>
                <Input type="email" value={newUserData.email} onChange={e => setNewUserData({...newUserData, email: e.target.value})} className="h-11 rounded-xl border-gray-200 bg-white" placeholder="email@exemple.com" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-700">Téléphone</Label>
                <Input value={newUserData.phone} onChange={e => setNewUserData({...newUserData, phone: e.target.value})} className="h-11 rounded-xl border-gray-200 bg-white" placeholder="0555..." />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-100">
              <Button type="button" variant="outline" onClick={() => setCreateUserDialogOpen(false)} className="h-11 rounded-xl px-6">Annuler</Button>
              <Button type="submit" className="h-11 rounded-xl px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-medium">Enregistrer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Profile Edit Dialog */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-0 shadow-2xl rounded-2xl" aria-describedby={undefined}>
          <DialogHeader className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-6 m-0">
            <DialogTitle className="text-2xl font-bold text-white">Modifier le profil</DialogTitle>
            <DialogDescription className="text-emerald-100 mt-1 text-sm">
              Mettez à jour les informations de l'utilisateur
            </DialogDescription>
          </DialogHeader>
          <div className="p-6 space-y-5 bg-gray-50/50">
            <div className="space-y-2"><Label className="text-sm font-semibold text-gray-700">Nom complet</Label><Input value={profileFormData.full_name} onChange={e => setProfileFormData({...profileFormData, full_name: e.target.value})} className="h-11 rounded-xl border-gray-200 bg-white" /></div>
            <div className="space-y-2"><Label className="text-sm font-semibold text-gray-700">Nom d'utilisateur</Label><Input value={profileFormData.username} onChange={e => setProfileFormData({...profileFormData, username: e.target.value})} className="h-11 rounded-xl border-gray-200 bg-white" /></div>
            <div className="space-y-2"><Label className="text-sm font-semibold text-gray-700">Email</Label><Input type="email" value={profileFormData.email} onChange={e => setProfileFormData({...profileFormData, email: e.target.value})} className="h-11 rounded-xl border-gray-200 bg-white" /></div>
            <div className="space-y-2"><Label className="text-sm font-semibold text-gray-700">Téléphone</Label><Input value={profileFormData.phone} onChange={e => setProfileFormData({...profileFormData, phone: e.target.value})} className="h-11 rounded-xl border-gray-200 bg-white" /></div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700">Rôle</Label>
              <Select value={profileFormData.role} onValueChange={(val) => setProfileFormData({...profileFormData, role: val})}>
                <SelectTrigger className="h-11 rounded-xl border-gray-200 bg-white">
                  <SelectValue placeholder="Sélectionner un rôle" />
                </SelectTrigger>
                <SelectContent>
                  {profileFormData.role === 'super_admin' && <SelectItem value="super_admin">Super Administrateur</SelectItem>}
                  {profileFormData.role === 'owner' && <SelectItem value="owner">Propriétaire</SelectItem>}
                  <SelectItem value="admin">Administrateur</SelectItem>
                  <SelectItem value="gerant">Gestionnaire</SelectItem>
                  <SelectItem value="comptable">Comptable</SelectItem>
                  <SelectItem value="caissier">Caissier</SelectItem>
                  <SelectItem value="assistant">Assistant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-between items-center gap-3 pt-6 mt-6 border-t border-gray-100">
              <span className="text-xs text-red-500 font-mono">Valeur à envoyer: {profileFormData.role}</span>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setProfileDialogOpen(false)} className="h-11 rounded-xl px-6">Annuler</Button>
                <Button onClick={handleProfileSubmit} className="h-11 rounded-xl px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-medium">Enregistrer</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-0 shadow-2xl rounded-2xl" aria-describedby={undefined}>
          <DialogHeader className="bg-gradient-to-r from-emerald-600 to-teal-700 px-6 py-6 m-0">
            <DialogTitle className="text-2xl font-bold text-white">Modifier le mot de passe</DialogTitle>
            <DialogDescription className="text-emerald-100 mt-1 text-sm">
              Définir un nouveau mot de passe pour cet utilisateur
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); handlePasswordChange(); }} autoComplete="off" className="p-6 space-y-5 bg-gray-50/50">
            {/* Champ caché pour guider le gestionnaire de mots de passe du navigateur */}
            <input type="text" name="username" value={selectedUser?.username || ''} style={{ display: 'none' }} autoComplete="username" readOnly />
            <div className="space-y-2">
              <Label htmlFor="new_password" className="text-sm font-semibold text-gray-700">Nouveau mot de passe</Label>
              <Input id="new_password" name="new_password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="h-11 rounded-xl border-gray-200 bg-white" autoComplete="new-password" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-700">Confirmer le mot de passe</Label>
              <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="h-11 rounded-xl border-gray-200 bg-white" autoComplete="new-password" />
            </div>
            <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-gray-100">
              <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)} className="h-11 rounded-xl px-6">Annuler</Button>
              <Button type="submit" className="h-11 rounded-xl px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-medium">Enregistrer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Permissions Dialog */}
      <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
        <DialogContent className="max-w-[1100px] h-[85vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between">
            <DialogTitle>Gérer les permissions : {selectedUser?.full_name || selectedUser?.username}</DialogTitle>
            <Button
              variant="outline"
              size="sm"
              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 border-orange-200"
              onClick={async () => {
                if (!window.confirm("Voulez-vous vraiment réinitialiser les permissions aux valeurs par défaut pour ce rôle ?")) return;
                try {
                  const res = await axios.put(`${API}/users/${selectedUser.id}/profile`, { reset_permissions: true }, { headers: getHeaders() });
                  toast.success('Permissions réinitialisées aux valeurs par défaut');
                  setPermissionsDialogOpen(false);
                  onRefresh();
                } catch (error) {
                  toast.error('Erreur lors de la réinitialisation');
                }
              }}
            >
              <RefreshCcw className="w-4 h-4 mr-2" />
              Rétablir les accès par défaut
            </Button>
          </DialogHeader>
          <div className="flex flex-1 overflow-hidden">
            <div className="w-[450px] border-r overflow-y-auto">
              <RoleModules 
                role={{id: 'custom'}}
                permissions={userPermissions}
                modules={PERMISSIONS_DEFS}
                selectedModule={selectedModule}
                setSelectedModule={setSelectedModule}
                loading={false}
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {selectedModule ? (
                <ModuleDetails 
                  module={selectedModule}
                  permissions={userPermissions}
                  onPermissionChange={handlePermissionChange}
                  onSetFullAccess={() => setModuleFullAccess(selectedModule.id)}
                  onSave={handleSaveUserPermissions}
                  saving={savingPermissions}
                />
              ) : (
                <div className="flex-1 flex items-center justify-center text-slate-400">
                  Sélectionnez un module
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default UsersTab;
